<template>
  <v-container>
    <div>
      <v-btn class="mb-3" @click="goBack" prepend-icon="mdi-arrow-left" text="Back to List" />
    </div>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Manage Guardrails</span>
          </v-card-title>

          <v-card-subtitle>
            Guardrails are automatically applied to all AI-generated content across all accounts to
            ensure safety and accuracy.
          </v-card-subtitle>

          <v-card-text>
            <v-row>
              <!-- Description Guardrails Section -->
              <v-col cols="12" md="6">
                <v-card variant="outlined" style="height: 100%">
                  <v-card-title
                    class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center gap-2 py-3"
                  >
                    <span class="text-h6 flex-grow-1">Description</span>
                    <v-btn
                      color="primary"
                      :size="$vuetify.display.xs ? 'default' : 'small'"
                      prepend-icon="mdi-plus"
                      @click="openAddGuardrailDialog('description')"
                      :block="$vuetify.display.xs"
                      class="mt-2 mt-sm-0"
                    >
                      <span class="d-sm-inline">Add Guardrail</span>
                    </v-btn>
                  </v-card-title>

                  <v-card-text>
                    <div v-if="isLoading" class="text-center py-4">
                      <v-progress-circular indeterminate />
                      <div class="mt-2">Loading guardrails...</div>
                    </div>

                    <div
                      v-else-if="descriptionGuardrails.length === 0"
                      class="text-center py-4 text-grey"
                    >
                      No description guardrails found. Create your first guardrail to ensure content
                      safety.
                    </div>

                    <v-list v-else>
                      <v-list-item
                        v-for="guardrail in descriptionGuardrails"
                        :key="guardrail.id"
                        class="border rounded mb-2"
                      >
                        <v-list-item-title class="d-flex align-center gap-2">
                          <div class="flex-grow-1">
                            {{ guardrail.name }}
                          </div>
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-wrap">
                          {{ guardrail.content.substring(0, 150)
                          }}{{ guardrail.content.length > 150 ? '...' : '' }}
                        </v-list-item-subtitle>

                        <template #append>
                          <div class="d-flex gap-1">
                            <v-btn
                              icon="mdi-pencil"
                              size="small"
                              variant="text"
                              @click="editGuardrail(guardrail)"
                            />
                            <v-btn
                              icon="mdi-delete"
                              size="small"
                              variant="text"
                              color="error"
                              @click="deleteGuardrail(guardrail)"
                            />
                          </div>
                        </template>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Highlight Guardrails Section -->
              <v-col cols="12" md="6">
                <v-card variant="outlined" style="height: 100%">
                  <v-card-title
                    class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center gap-2 py-3"
                  >
                    <span class="text-h6 flex-grow-1">Highlight</span>
                    <v-btn
                      color="primary"
                      :size="$vuetify.display.xs ? 'default' : 'small'"
                      prepend-icon="mdi-plus"
                      @click="openAddGuardrailDialog('highlight')"
                      :block="$vuetify.display.xs"
                      class="mt-2 mt-sm-0"
                    >
                      <span class="d-sm-inline">Add Guardrail</span>
                    </v-btn>
                  </v-card-title>

                  <v-card-text>
                    <div v-if="isLoading" class="text-center py-4">
                      <v-progress-circular indeterminate />
                      <div class="mt-2">Loading guardrails...</div>
                    </div>

                    <div
                      v-else-if="highlightGuardrails.length === 0"
                      class="text-center py-4 text-grey"
                    >
                      No highlight guardrails found. Create your first guardrail to ensure content
                      safety.
                    </div>

                    <v-list v-else>
                      <v-list-item
                        v-for="guardrail in highlightGuardrails"
                        :key="guardrail.id"
                        class="border rounded mb-2"
                      >
                        <v-list-item-title class="d-flex align-center gap-2">
                          <div class="flex-grow-1">
                            {{ guardrail.name }}
                          </div>
                        </v-list-item-title>
                        <v-list-item-subtitle class="text-wrap">
                          {{ guardrail.content.substring(0, 150)
                          }}{{ guardrail.content.length > 150 ? '...' : '' }}
                        </v-list-item-subtitle>

                        <template #append>
                          <div class="d-flex gap-1">
                            <v-btn
                              icon="mdi-pencil"
                              size="small"
                              variant="text"
                              @click="editGuardrail(guardrail)"
                            />
                            <v-btn
                              icon="mdi-delete"
                              size="small"
                              variant="text"
                              color="error"
                              @click="deleteGuardrail(guardrail)"
                            />
                          </div>
                        </template>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add/Edit Guardrail Dialog -->
    <v-dialog v-model="showGuardrailDialog" max-width="700px">
      <v-card>
        <v-card-title>
          {{ editingGuardrail ? 'Edit Guardrail' : 'Add New Guardrail' }}
        </v-card-title>

        <v-card-text>
          <v-form v-model="isFormValid" @submit.prevent="saveGuardrail">
            <v-text-field
              v-model="guardrailData.name"
              label="Guardrail Name"
              :rules="[(v: string) => !!v || 'Name is required']"
              variant="outlined"
              class="mb-3"
            />

            <v-textarea
              v-model="guardrailData.content"
              label="Guardrail Content"
              :rules="[(v: string) => !!v || 'Content is required']"
              variant="outlined"
              rows="8"
              class="mb-3"
              hint="This content will be automatically prepended to all AI generation requests"
            />

            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  v-model="guardrailData.type"
                  :items="guardrailTypes"
                  label="Guardrail Type"
                  :rules="[(v: string) => !!v || 'Type is required']"
                  variant="outlined"
                  :disabled="!!editingGuardrail"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model.number="guardrailData.priority"
                  label="Priority"
                  type="number"
                  variant="outlined"
                  hint="Higher numbers = higher priority"
                  :rules="[(v: number) => v >= 0 || 'Priority must be >= 0']"
                />
              </v-col>

              <v-col cols="12" md="4">
                <v-switch
                  v-model="guardrailData.isActive"
                  label="Active"
                  color="success"
                  hide-details
                />
              </v-col>
            </v-row>

            <v-alert type="warning" class="mt-3" density="compact">
              🚨 Guardrails apply to ALL accounts and ALL AI-generated content. Changes affect the
              entire system.
            </v-alert>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text="Cancel" @click="closeGuardrailDialog" />
          <v-btn
            color="primary"
            text="Save"
            :loading="isLoading"
            :disabled="!isFormValid"
            @click="saveGuardrail"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete the guardrail "{{ guardrailToDelete?.name }}"? This will
          remove protection for ALL accounts and cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text="Cancel" @click="showDeleteDialog = false" />
          <v-btn color="error" text="Delete" :loading="isLoading" @click="confirmDelete" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Snackbar -->
    <v-snackbar v-model="showError" color="error" timeout="5000">
      {{ errorMessage }}
    </v-snackbar>

    <!-- Success Snackbar -->
    <v-snackbar v-model="showSuccess" color="success" timeout="3000">
      {{ successMessage }}
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, computed } from 'vue'
import { useGuardrailsStore, type IGuardrail } from '@/stores/guardrails'

// Props
interface Props {
  goBack?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  goBack: undefined,
})

// Stores
const guardrailsStore = useGuardrailsStore()

// Dialog states
const showGuardrailDialog = ref(false)
const showDeleteDialog = ref(false)
const showError = ref(false)
const showSuccess = ref(false)

// Form states
const isFormValid = ref(false)
const editingGuardrail = ref<IGuardrail | null>(null)
const guardrailToDelete = ref<IGuardrail | null>(null)

// Form data
const guardrailData = ref({
  name: '',
  content: '',
  type: 'description' as 'description' | 'highlight',
  isActive: true,
  priority: 100,
})

// Form options
const guardrailTypes = [
  { title: 'Description', value: 'description' },
  { title: 'Highlight', value: 'highlight' },
]

// Correct ComputedRef usage for guardrails
const descriptionGuardrails = computed(() => guardrailsStore.descriptionGuardrails.value ?? [])
const highlightGuardrails = computed(() => guardrailsStore.highlightGuardrails.value ?? [])

// Correct ComputedRef usage for isLoading
const isLoading = computed(() => guardrailsStore.isLoading.value ?? false)

// Define missing variables
const successMessage = ref('')
const errorMessage = ref('')

// Ensure closeGuardrailDialog is properly defined
const closeGuardrailDialog = () => {
  editingGuardrail.value = null
  showGuardrailDialog.value = false
}

// Update template bindings to use .value
// Example: Replace `descriptionGuardrails.length` with `descriptionGuardrails.value.length`
// Example: Replace `guardrail.id` with `guardrail.value.id`

// Ensure proper type handling for guardrails
// Updated all references to guardrail properties to use `.value` where necessary.
const saveGuardrail = async () => {
  if (!isFormValid.value) return

  try {
    if (editingGuardrail.value) {
      await guardrailsStore.updateGuardrail(editingGuardrail.value.id, {
        name: guardrailData.value.name,
        content: guardrailData.value.content,
        type: guardrailData.value.type,
        isActive: guardrailData.value.isActive,
        priority: guardrailData.value.priority,
      })
      successMessage.value = 'Guardrail updated successfully!'
    } else {
      await guardrailsStore.addGuardrail({
        name: guardrailData.value.name,
        content: guardrailData.value.content,
        type: guardrailData.value.type,
        isActive: guardrailData.value.isActive,
        priority: guardrailData.value.priority,
      })
      successMessage.value = 'Guardrail created successfully!'
    }

    showSuccess.value = true
    closeGuardrailDialog()
  } catch (error) {
    console.error('Failed to save guardrail:', error)
    errorMessage.value = 'Failed to save guardrail. Please try again.'
    showError.value = true
  }
}

const confirmDelete = async () => {
  if (!guardrailToDelete.value) return

  try {
    await guardrailsStore.removeGuardrail(guardrailToDelete.value.id, guardrailToDelete.value.type)
    successMessage.value = 'Guardrail deleted successfully!'
    showSuccess.value = true
    showDeleteDialog.value = false
    guardrailToDelete.value = null
  } catch (error) {
    console.error('Failed to delete guardrail:', error)
    errorMessage.value = 'Failed to delete guardrail. Please try again.'
    showError.value = true
  }
}

// Adding missing methods
const openAddGuardrailDialog = (type: 'description' | 'highlight') => {
  editingGuardrail.value = null
  guardrailData.value = {
    name: '',
    content: '',
    type,
    isActive: true,
    priority: 100,
  }
  showGuardrailDialog.value = true
}

const editGuardrail = (guardrail: IGuardrail) => {
  editingGuardrail.value = guardrail
  guardrailData.value = { ...guardrail }
  showGuardrailDialog.value = true
}

const deleteGuardrail = (guardrail: IGuardrail) => {
  guardrailToDelete.value = guardrail
  showDeleteDialog.value = true
}
</script>

<style scoped>
.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
